import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ICON_STROKE_WIDTH } from '../Icon';

type Feature = { step: string; title?: string; content: string; image: string; imagePosition?: string };

type FeatureStepsProps = {
  features: Feature[];
  className?: string;
  title?: string;
  autoPlayInterval?: number;
  imageHeight?: string;
};

const cx = (...classes: Array<string | undefined | false>) => classes.filter(Boolean).join(' ');

export function FeatureSteps({ features, className, title, autoPlayInterval = 5000, imageHeight = 'min-h-[430px] md:min-h-[540px] lg:min-h-[620px]' }: FeatureStepsProps) {
  const [currentFeature, setCurrentFeature] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(() => typeof window === 'undefined' || !window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncPreference = () => setIsPlaying(!media.matches);
    media.addEventListener('change', syncPreference);
    return () => media.removeEventListener('change', syncPreference);
  }, []);

  useEffect(() => {
    if (!isPlaying || features.length < 2) return;
    const timer = window.setInterval(() => {
      setProgress((previous) => {
        const next = previous + 100 / (autoPlayInterval / 100);
        if (next >= 100) {
          setCurrentFeature((index) => (index + 1) % features.length);
          return 0;
        }
        return next;
      });
    }, 100);
    return () => window.clearInterval(timer);
  }, [autoPlayInterval, features.length, isPlaying]);

  if (features.length === 0) return null;
  const activeFeature = features[currentFeature];
  const selectFeature = (index: number) => { setCurrentFeature(index); setProgress(0); };

  return (
    <div className={cx('mt-12 lg:mt-16', className)} data-od-id="speaker-carousel">
      {title ? <h3 className="mb-10 text-center text-3xl font-semibold tracking-[-0.04em] text-[var(--fg)] sm:text-4xl">{title}</h3> : null}
      <div className="grid gap-6 md:grid-cols-[minmax(250px,.78fr)_minmax(0,1.22fr)] md:gap-10 lg:gap-14">
        <div className="order-2 space-y-2 md:order-1 md:self-center">
          <div className="mb-4 flex items-center justify-between gap-4 px-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--muted)]">Speaker perspectives</p>
            <button type="button" onClick={() => setIsPlaying((playing) => !playing)} className="min-h-11 rounded-full border border-[var(--border)] bg-white px-4 text-xs font-semibold tracking-[0.02em] text-[var(--fg)] transition-colors duration-150 hover:border-[var(--fg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]" aria-pressed={isPlaying} data-od-id="speaker-carousel-toggle">
              {isPlaying ? 'Pause rotation' : 'Resume rotation'}
            </button>
          </div>
          {features.map((feature, index) => {
            const isCurrent = index === currentFeature;
            return (
              <button key={feature.step} type="button" onClick={() => { selectFeature(index); setIsPlaying(false); }} className={cx('group relative flex w-full items-start gap-4 rounded-2xl p-4 text-left transition-colors duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] sm:gap-5 sm:p-5', isCurrent ? 'bg-white shadow-[0_16px_40px_rgba(17,24,39,.08)]' : 'hover:bg-white/70')} aria-current={isCurrent ? 'true' : undefined}>
                <span className={cx('relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full border-2 text-sm font-semibold transition-all duration-500 md:h-10 md:w-10', isCurrent ? 'border-[var(--accent)] bg-[var(--accent)] text-white' : 'border-[var(--border)] bg-white text-[var(--muted)]')}>
                  {index < currentFeature ? <Check className="h-4 w-4" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" /> : String(index + 1).padStart(2, '0')}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--accent)]">Confirmed speaker</span>
                  <span className="mt-1 block text-xl font-semibold tracking-[-0.035em] text-[var(--fg)] sm:text-2xl">{feature.title || feature.step}</span>
                  <span className="mt-2 block text-sm leading-6 text-[var(--muted)] sm:text-[15px]">{feature.content}</span>
                </span>
                {isCurrent ? <motion.span className="absolute bottom-0 left-5 h-0.5 bg-[var(--accent)]" initial={{ width: '0%' }} animate={{ width: `${progress}%` }} transition={{ ease: 'linear', duration: 0.1 }} aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
        <div className={cx('order-1 relative overflow-hidden rounded-[28px] bg-[var(--fg)] shadow-[0_28px_80px_rgba(17,24,39,.16)] md:order-2', imageHeight)}>
          <AnimatePresence mode="wait">
            <motion.figure key={activeFeature.step} className="absolute inset-0" initial={{ y: 50, opacity: 0, rotateX: -8 }} animate={{ y: 0, opacity: 1, rotateX: 0 }} exit={{ y: -50, opacity: 0, rotateX: 8 }} transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}>
              <img src={activeFeature.image} alt={activeFeature.title || activeFeature.step} className="h-full w-full object-cover" style={{ objectPosition: activeFeature.imagePosition ?? 'center' }} width="996" height="1495" loading="eager" />
              <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/85 via-black/30 to-transparent" aria-hidden="true" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-5 text-white sm:p-7 lg:p-9"><p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/65">{activeFeature.step}</p><p className="mt-2 max-w-[20ch] text-[clamp(1.75rem,3vw,3rem)] font-medium leading-[.98] tracking-[-0.045em]">{activeFeature.title}</p></figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
